import React, { useEffect, useRef, useState } from 'react'
import { MagnifyingGlassIcon, CheckCircleIcon } from '@heroicons/react/24/outline'
import { opcjeModeli, szukajModeli } from '../../api/falowniki'

/**
 * Wybór konkretnego modelu z katalogu: przez zawężenie, przez nazwę albo przez
 * podanie prądu wprost.
 *
 * ═══ TRZY DROGI, BO LUDZIE PAMIĘTAJĄ RÓŻNE RZECZY ═══
 * Nazwa modelu jest najprecyzyjniejsza, ale najgorzej zapamiętana. Moc zna
 * prawie każdy, bo stoi na fakturze i w umowie przyłączeniowej. Dlatego pole
 * z lupą nie jest jedynym wejściem: obok stoją listy zawężające, a przy module
 * także możliwość podania samego prądu Impp.
 *
 * ═══ ZNACZNIK KARTY ═══
 * Przy każdej podpowiedzi widać, czy mamy dla niej parametry elektryczne.
 * Bez tego użytkownik wybrałby model, o którym wiemy tylko tyle, co nazwa,
 * i dostał dobór po samej mocy, nie rozumiejąc, dlaczego jest mniej dokładny.
 */

const KLASA_POLA = `border-2 border-blue-200 bg-blue-50/40 rounded-lg px-3 py-2.5 text-sm
                    focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500`

const WyborModelu = ({ pytanie, wartosc, onOdpowiedz, mocZOdpowiedzi }) => {
  const { kategoria, filtry = [], alternatywa } = pytanie
  const jednostkaMocy = kategoria === 'inwerter' ? 'kW' : 'W'

  const [fraza, setFraza] = useState(wartosc?.model?.etykieta ?? '')
  const [prad, setPrad] = useState(wartosc?.impp ?? '')
  const [zawezenie, setZawezenie] = useState({ moc: '', rodzaj: '', producent: '' })
  const [opcje, setOpcje] = useState(null)
  const [wyniki, setWyniki] = useState([])
  const [szuka, setSzuka] = useState(false)
  const ostatnie = useRef(0)

  useEffect(() => {
    if (!filtry.length) return
    opcjeModeli(kategoria).then(setOpcje).catch(() => setOpcje(null))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kategoria])

  const wybrany = wartosc?.model
  const cosZawezone = Object.values(zawezenie).some(Boolean)
  const frazaGotowa = fraza.trim().length >= 3 && fraza !== wybrany?.etykieta

  useEffect(() => {
    if (wybrany || (!frazaGotowa && !cosZawezone)) { setWyniki([]); return undefined }
    // Odpytujemy dopiero po chwili bezruchu — inaczej każde wciśnięcie klawisza
    // to osobne zapytanie, a odpowiedzi wracają w losowej kolejności.
    const t = setTimeout(async () => {
      const moje = ++ostatnie.current
      setSzuka(true)
      try {
        const r = await szukajModeli(kategoria, {
          q: frazaGotowa ? fraza.trim() : undefined,
          // Moc podana krok wcześniej zawęża listę modułów sama z siebie —
          // po niej wpisanie marki wystarczy, żeby trafić w jeden model.
          moc: zawezenie.moc || mocZOdpowiedzi || undefined,
          rodzaj: zawezenie.rodzaj || undefined,
          producent: zawezenie.producent || undefined,
        })
        if (moje === ostatnie.current) setWyniki(r.wyniki || [])
      } catch { if (moje === ostatnie.current) setWyniki([]) } finally {
        if (moje === ostatnie.current) setSzuka(false)
      }
    }, 350)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fraza, zawezenie, mocZOdpowiedzi, kategoria, wybrany])

  const wybierz = (w) => {
    setFraza(w.nazwa); setWyniki([])
    onOdpowiedz({ model: { id: w.model_id, etykieta: w.nazwa, ma_karte: w.ma_karte } })
  }

  const zatwierdzPrad = () => {
    if (prad === '') return
    onOdpowiedz({ impp: Number(String(prad).replace(',', '.')) })
  }

  return (
    <div>
      {/* ═══ ZAWĘŻENIE ═══ trzy listy w jednej linii, nad polem z lupą. */}
      {filtry.length > 0 && !wybrany && (
        <>
          <div className="grid sm:grid-cols-3 gap-2">
            {filtry.includes('moc') && (
              <select value={zawezenie.moc} aria-label="Moc"
                      onChange={(e) => setZawezenie((z) => ({ ...z, moc: e.target.value }))}
                      className={KLASA_POLA}>
                <option value="">Moc…</option>
                {(opcje?.moce || []).map((m) => (
                  <option key={m} value={m}>
                    {String(m).replace('.', ',')} {jednostkaMocy}
                  </option>
                ))}
              </select>
            )}
            {filtry.includes('rodzaj') && (
              <select value={zawezenie.rodzaj} aria-label="Rodzaj"
                      onChange={(e) => setZawezenie((z) => ({ ...z, rodzaj: e.target.value }))}
                      className={KLASA_POLA}>
                <option value="">Rodzaj…</option>
                {(opcje?.rodzaje || []).map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            )}
            {filtry.includes('producent') && (
              <select value={zawezenie.producent} aria-label="Producent"
                      onChange={(e) => setZawezenie((z) => ({ ...z, producent: e.target.value }))}
                      className={KLASA_POLA}>
                <option value="">Producent…</option>
                {(opcje?.producenci || []).map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            )}
          </div>
          <div className="flex items-center gap-3 my-3">
            <span className="h-px flex-1 bg-gray-200" />
            <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">lub</span>
            <span className="h-px flex-1 bg-gray-200" />
          </div>
        </>
      )}

      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="relative w-full sm:w-72">
          <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text" value={fraza} onChange={(e) => setFraza(e.target.value)}
            placeholder={pytanie.placeholder} aria-label={pytanie.tytul}
            className={`w-full ${KLASA_POLA} pl-9`}
          />
        </div>

        {/* ═══ ALTERNATYWA: SAM PRĄD ═══ dla kogoś, kto ma kartę pod ręką albo
            stoi przy module i czyta z naklejki, ale nazwy nie zna. */}
        {alternatywa === 'prad' && !wybrany && (
          <>
            <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">lub</span>
            <span className="flex items-center gap-2">
              <span className="relative w-32">
                <input
                  type="text" inputMode="decimal" value={prad}
                  onChange={(e) => setPrad(e.target.value)}
                  onBlur={zatwierdzPrad}
                  onKeyDown={(e) => { if (e.key === 'Enter') zatwierdzPrad() }}
                  placeholder="np. 13,4" aria-label="Prąd Impp"
                  className={`w-full text-center tabular-nums ${KLASA_POLA} pr-8`}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">
                  A
                </span>
              </span>
              <span className="text-xs text-gray-500">prąd Impp</span>
            </span>
          </>
        )}

      </div>

      {wybrany && (
        <p className="mt-2 text-xs text-gray-600 flex items-start gap-1.5">
          <CheckCircleIcon className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-px" />
          {wybrany.ma_karte
            ? 'Mamy kartę katalogową tego modelu, więc dobierzemy po prądzie.'
            : 'Rozpoznany, ale bez karty katalogowej. Podaj obok prąd Impp albo pomiń.'}
        </p>
      )}
      {!wybrany && wartosc?.impp && (
        <p className="mt-2 text-xs text-gray-600 flex items-start gap-1.5">
          <CheckCircleIcon className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-px" />
          Przyjęliśmy prąd {String(wartosc.impp).replace('.', ',')} A.
        </p>
      )}

      {!wybrany && (frazaGotowa || cosZawezone) && (
        <div className="mt-2 rounded-xl border border-gray-200 divide-y divide-gray-100 overflow-hidden">
          {szuka && <p className="px-3 py-2.5 text-xs text-gray-400">Szukam…</p>}
          {!szuka && wyniki.length === 0 && (
            <p className="px-3 py-2.5 text-xs text-gray-500">
              Nic nie znaleźliśmy. Spróbuj samej marki albo pomiń to pytanie.
            </p>
          )}
          {wyniki.map((w) => (
            <button key={w.model_id} type="button" onClick={() => wybierz(w)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-50">
              <span className="min-w-0 flex-1">
                <span className="block text-sm text-gray-900 truncate">{w.nazwa}</span>
                <span className="block text-xs text-gray-500">
                  {/* Moc modułu stoi w katalogu w watach, moc falownika w kilowatach. */}
                  {w.producent}
                  {w.moc ? ` · ${String(w.moc).replace('.', ',')} ${jednostkaMocy}` : ''}
                  {w.imp_a ? ` · Impp ${String(w.imp_a).replace('.', ',')} A` : ''}
                </span>
              </span>
              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded flex-shrink-0 ${
                w.ma_karte ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'
              }`}>
                {w.ma_karte ? 'z kartą' : 'bez karty'}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default WyborModelu
